import {Injectable} from '@angular/core';
import {DocumentModel} from "../../model/document.model";
import {Container} from "../../model/layout.model";
import {LayoutElementUtil} from "./layout-element.service";

@Injectable({
  providedIn: 'root'
})
export class CompletenessCheckService {

  //Reiter je Rundfunkanstalt, die vor der Veröffentlichung auf Vollständigkeit geprüft werden
  private readonly TABS_TO_CHECK: { [rfaKuerzel: string]: string[] } = {
    sr: ['Tag', 'Nacht', 'Aussichten', 'Großregion']
  };

  private readonly WETTERSYMBOL_ITEMS_URL: string = '/wettersymbol/';
  private readonly EMPTY_COMBOBOX_CODE: string = '-1';
  private readonly KEIN_SYMBOL_CODE: string = '0';

  constructor(private layoutElementUtil: LayoutElementUtil) {
  }

  /**
   * Liefert die Captions der Reiter, in denen Wettersymbole/Temperaturen nur teilweise ausgefüllt sind.
   * Ist in einem Reiter kein einziges dieser Felder ausgefüllt, gilt er als vollständig. Warnschilder werden ignoriert.
   * Ist überall das Symbol "0 - keins" gewählt und keine Temperatur ausgefüllt, gilt er ebenfalls als vollständig.
   */
  findIncompleteTabs(document: DocumentModel, rfaKuerzel: string | undefined): string[] {
    const tabsToCheck = rfaKuerzel ? this.TABS_TO_CHECK[rfaKuerzel.toLowerCase()] : undefined;
    if (!tabsToCheck) return [];

    return document.tabs
      .filter(tab => tabsToCheck.includes(tab.caption))
      .filter(tab => !this.isTabComplete(tab))
      .map(tab => tab.caption);
  }

  private isTabComplete(tab: Container): boolean {
    const fields = this.collectRelevantFields(tab);
    const filledFields = fields.filter(field => this.isFilled(field));
    if (filledFields.length === 0 || filledFields.length === fields.length) return true;

    //Überall "0 - keins" als Symbol und keine Temperaturen gilt ebenfalls als vollständig
    const symbols = fields.filter(field => this.layoutElementUtil.isComboBox(field));
    const onlyKeinSymbol = symbols.every(symbol => `${this.layoutElementUtil.asComboBox(symbol).selectedItem?.code}` === this.KEIN_SYMBOL_CODE);
    return onlyKeinSymbol && filledFields.length === symbols.length;
  }

  private collectRelevantFields(container: Container): Container[] {
    const fields: Container[] = this.isWetterSymbol(container) || this.layoutElementUtil.isTemperatureTextField(container) ? [container] : [];
    container.containerList?.forEach(child => fields.push(...this.collectRelevantFields(child)));
    return fields;
  }

  private isWetterSymbol = (container: Container) =>
    this.layoutElementUtil.isComboBox(container) && this.layoutElementUtil.asComboBox(container).itemsUrl?.includes(this.WETTERSYMBOL_ITEMS_URL);

  private isFilled(container: Container): boolean {
    if (this.layoutElementUtil.isComboBox(container)) {
      const code = this.layoutElementUtil.asComboBox(container).selectedItem?.code;
      return code !== undefined && code !== null && `${code}` !== '' && `${code}` !== this.EMPTY_COMBOBOX_CODE;
    }
    const content = this.layoutElementUtil.asTemperatureTextField(container).content;
    return content !== undefined && content !== null && `${content}`.trim() !== '';
  }
}
